import React from 'react';
import './AudioLevelMeter.css';

interface AudioLevelMeterProps {
  level: number; // 0 to 100
  isActive: boolean;
  sensitivity: number; // 0.5 to 2.0
  onSensitivityChange: (sensitivity: number) => void;
}

const AudioLevelMeter: React.FC<AudioLevelMeterProps> = ({ level, isActive, sensitivity, onSensitivityChange }) => {
  const getBarColor = (barIndex: number, currentLevel: number): string => {
    const barLevel = (barIndex + 1) * 5; // Each bar represents 5% of max level

    if (barLevel <= currentLevel) {
      if (barLevel <= 30) return '#28a745'; // Green for low levels
      if (barLevel <= 70) return '#ffc107'; // Yellow for medium levels
      return '#dc3545'; // Red for high levels
    }

    return '#e9ecef'; // Gray for inactive bars
  };

  const getLevelText = (level: number): string => {
    if (level < 5) return 'Very Quiet';
    if (level < 15) return 'Quiet';
    if (level < 30) return 'Good';
    if (level < 60) return 'Strong';
    if (level < 85) return 'Very Strong';
    return 'Too Loud';
  };

  const getLevelAdvice = (level: number): string => {
    if (level < 5) return 'Play louder or move closer to mic';
    if (level < 15) return 'Try playing a bit louder';
    if (level < 30) return 'Perfect level for detection';
    if (level < 85) return 'Good signal strength';
    return 'May cause distortion - play softer';
  };

  if (!isActive) {
    return (
      <div className="audio-level-meter inactive">
        <div className="sensitivity-control">
          <label htmlFor="sensitivity">Sensitivity:</label>
          <input
            id="sensitivity"
            type="range"
            min="0.5"
            max="3.0"
            step="0.1"
            value={sensitivity}
            onChange={(e) => onSensitivityChange(parseFloat(e.target.value))}
            className="sensitivity-slider"
          />
          <span className="sensitivity-value">{sensitivity.toFixed(1)}x</span>
        </div>
        <div className="meter-header">
          <span className="meter-title">Audio Level</span>
        </div>
        <div className="level-bars">
          {Array.from({ length: 20 }, (_, i) => (
            <div key={i} className="level-bar" style={{ backgroundColor: '#e9ecef' }} />
          ))}
        </div>
        <div className="level-info">
          <span className="level-text">Not listening</span>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-level-meter active">
      <div className="sensitivity-control">
        <label htmlFor="sensitivity">Sensitivity:</label>
        <input
          id="sensitivity"
          type="range"
          min="0.5"
          max="3.0"
          step="0.1"
          value={sensitivity}
          onChange={(e) => onSensitivityChange(parseFloat(e.target.value))}
          className="sensitivity-slider"
        />
        <span className="sensitivity-value">{sensitivity.toFixed(1)}x</span>
      </div>
      <div className="meter-header">
        <span className="meter-title">Audio Level</span>
        <span className="level-percentage">{Math.round(level)}%</span>
      </div>

      <div className="level-bars">
        {Array.from({ length: 20 }, (_, i) => (
          <div
            key={i}
            className="level-bar"
            style={{ backgroundColor: getBarColor(i, level) }}
          />
        ))}
      </div>

      <div className="level-info">
        <div className="level-text">{getLevelText(level)}</div>
        <div className="level-advice">{getLevelAdvice(level)}</div>
      </div>
    </div>
  );
};

export default AudioLevelMeter;
